import React, { useState, useEffect, FormEvent } from 'react';
import { apiClient } from '../../api/client';

// ── Types ─────────────────────────────────────────────────────

type ActionItemStatus = 'Open' | 'InProgress' | 'Completed' | 'Cancelled';
type ActionItemPriority = 'High' | 'Medium' | 'Low';

interface FollowUp {
  id: string;
  actionItemId: string;
  note: string;
  createdBy: string;
  createdAt: string;
}

interface ActionItem {
  id: string;
  meetingId: string;
  title: string;
  description: string | null;
  assigneeId: string;
  assignee: { id: string; firstName: string; lastName: string };
  dueDate: string;
  status: ActionItemStatus;
  priority: ActionItemPriority;
  completedAt: string | null;
  overdue: boolean;
  followUps: FollowUp[];
  createdAt: string;
  updatedAt: string;
}

export interface ActionItemsTabProps {
  meetingId: string;
  subcommitteeId: string | null;
  canEdit: boolean;
  actionItems?: ActionItem[];
}

// ── Constants ─────────────────────────────────────────────────

const PRIORITY_COLORS: Record<ActionItemPriority, string> = {
  High: '#d32f2f',
  Medium: '#f57c00',
  Low: '#1976d2',
};

const STATUS_COLORS: Record<ActionItemStatus, string> = {
  Open: '#1976d2',
  InProgress: '#f57c00',
  Completed: '#1a6b3c',
  Cancelled: '#9e9e9e',
};

const STATUS_TRANSITIONS: Record<ActionItemStatus, { label: string; target: ActionItemStatus }[]> = {
  Open: [
    { label: 'Start', target: 'InProgress' },
    { label: 'Cancel', target: 'Cancelled' },
  ],
  InProgress: [
    { label: 'Complete', target: 'Completed' },
    { label: 'Cancel', target: 'Cancelled' },
  ],
  Completed: [],
  Cancelled: [],
};

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '0.5rem 0' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' },
  sectionTitle: { fontSize: '1rem', fontWeight: 600, color: '#333', margin: 0 },
  addBtn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' },
  // Form styles
  formCard: { backgroundColor: '#f9fafb', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' },
  formTitle: { fontSize: '0.9rem', fontWeight: 600, color: '#1a6b3c', marginBottom: '0.75rem', margin: '0 0 0.75rem 0' },
  formRow: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' as const, marginBottom: '0.75rem' },
  formField: { flex: '1 1 200px', minWidth: '150px' },
  formFieldFull: { flex: '1 1 100%' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#555', marginBottom: '4px' },
  input: { width: '100%', padding: '0.5rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' as const },
  textarea: { width: '100%', padding: '0.5rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' as const, minHeight: '60px', resize: 'vertical' as const, fontFamily: 'inherit' },
  select: { width: '100%', padding: '0.5rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', backgroundColor: '#fff', boxSizing: 'border-box' as const },
  formBtnRow: { display: 'flex', gap: '0.5rem', marginTop: '0.5rem' },
  submitBtn: { padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer' },
  cancelBtn: { padding: '0.5rem 1rem', backgroundColor: '#f5f5f5', color: '#333', border: '1px solid #e0e0e0', borderRadius: '4px', fontSize: '0.8rem', cursor: 'pointer' },
  // Action item list
  itemCard: { border: '1px solid #e0e0e0', borderRadius: '8px', marginBottom: '0.75rem', backgroundColor: '#fff', overflow: 'hidden' },
  itemHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', cursor: 'pointer', gap: '0.5rem', flexWrap: 'wrap' as const },
  itemLeft: { display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 },
  itemTitle: { fontSize: '0.9rem', fontWeight: 600, color: '#333', whiteSpace: 'nowrap' as const, overflow: 'hidden', textOverflow: 'ellipsis' },
  itemMeta: { display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' as const, flexShrink: 0 },
  badge: { display: 'inline-block', padding: '0.15rem 0.5rem', borderRadius: '3px', fontSize: '0.7rem', fontWeight: 600, color: '#fff' },
  overdueText: { fontSize: '0.7rem', fontWeight: 700, color: '#d32f2f' },
  assigneeText: { fontSize: '0.8rem', color: '#555' },
  dueDateText: { fontSize: '0.8rem', color: '#555' },
  // Expanded section
  expandedSection: { borderTop: '1px solid #e0e0e0', padding: '1rem', backgroundColor: '#fafafa' },
  descriptionText: { fontSize: '0.85rem', color: '#555', marginBottom: '0.75rem' },
  statusControls: { display: 'flex', gap: '0.4rem', marginBottom: '1rem', flexWrap: 'wrap' as const },
  statusBtn: { padding: '0.3rem 0.6rem', border: 'none', borderRadius: '3px', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer', color: '#fff' },
  // Follow-ups
  followUpsSection: { marginTop: '1rem' },
  followUpsTitle: { fontSize: '0.85rem', fontWeight: 600, color: '#333', marginBottom: '0.5rem' },
  followUpItem: { padding: '0.5rem 0.75rem', borderLeft: '3px solid #1a6b3c', marginBottom: '0.5rem', backgroundColor: '#fff', borderRadius: '0 4px 4px 0' },
  followUpNote: { fontSize: '0.85rem', color: '#333', marginBottom: '0.25rem' },
  followUpMeta: { fontSize: '0.7rem', color: '#888' },
  followUpForm: { display: 'flex', gap: '0.5rem', marginTop: '0.75rem', alignItems: 'flex-start' },
  followUpInput: { flex: 1, padding: '0.5rem 0.6rem', border: '1px solid #ccc', borderRadius: '4px', fontSize: '0.85rem', boxSizing: 'border-box' as const, minHeight: '36px', resize: 'vertical' as const, fontFamily: 'inherit' },
  followUpBtn: { padding: '0.5rem 0.75rem', backgroundColor: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' as const },
  // States
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: '#999', fontSize: '0.9rem' },
  error: { color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '0.8rem' },
  loading: { textAlign: 'center' as const, padding: '1.5rem', color: '#666', fontSize: '0.9rem' },
};

// ── Component ─────────────────────────────────────────────────

export function ActionItemsTab({ meetingId, subcommitteeId, canEdit }: ActionItemsTabProps) {
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [members, setMembers] = useState<{ id: string; firstName: string; lastName: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Form state
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formAssigneeId, setFormAssigneeId] = useState('');
  const [formDueDate, setFormDueDate] = useState('');
  const [formPriority, setFormPriority] = useState<ActionItemPriority>('Medium');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Follow-up state
  const [followUpNote, setFollowUpNote] = useState('');
  const [followUpSubmitting, setFollowUpSubmitting] = useState(false);

  // ── Data Fetching ─────────────────────────────────────────

  useEffect(() => {
    fetchActionItems();
    if (subcommitteeId) {
      fetchMembers();
    }
  }, [meetingId, subcommitteeId]);

  async function fetchActionItems() {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<ActionItem[]>(`/meetings/action-items?meetingId=${meetingId}`);
      setActionItems(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load action items.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchMembers() {
    try {
      const affiliations = await apiClient<Array<{
        member: { id: string; firstName: string; lastName: string };
      }>>(`/subcommittees/${subcommitteeId}/affiliations`);
      setMembers(affiliations.map((a) => a.member));
    } catch {
      // Non-critical; dropdown will be empty
      setMembers([]);
    }
  }

  // ── Actions ───────────────────────────────────────────────

  async function handleCreateActionItem(e: FormEvent) {
    e.preventDefault();
    setFormError('');

    if (!formTitle.trim()) {
      setFormError('Title is required.');
      return;
    }
    if (!formAssigneeId) {
      setFormError('Assignee is required.');
      return;
    }
    if (!formDueDate) {
      setFormError('Due date is required.');
      return;
    }

    setSubmitting(true);
    try {
      const newItem = await apiClient<ActionItem>(`/meetings/${meetingId}/action-items`, {
        method: 'POST',
        body: {
          title: formTitle.trim(),
          description: formDescription.trim() || null,
          assigneeId: formAssigneeId,
          dueDate: formDueDate,
          priority: formPriority,
        },
      });
      setActionItems((prev) => [{ ...newItem, overdue: false, followUps: newItem.followUps || [] }, ...prev]);
      resetForm();
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setFormError(apiErr.message || 'Failed to create action item.');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleStatusChange(itemId: string, newStatus: ActionItemStatus) {
    try {
      const updated = await apiClient<ActionItem>(`/meetings/action-items/${itemId}`, {
        method: 'PATCH',
        body: { status: newStatus },
      });
      setActionItems((prev) =>
        prev.map((item) => {
          if (item.id === itemId) {
            const now = new Date();
            const dueDate = new Date(item.dueDate);
            const overdue =
              (updated.status === 'Open' || updated.status === 'InProgress') &&
              dueDate < now;
            return { ...item, ...updated, overdue, followUps: updated.followUps || item.followUps };
          }
          return item;
        }),
      );
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update status.');
    }
  }

  async function handleAddFollowUp(actionItemId: string) {
    if (!followUpNote.trim()) return;

    setFollowUpSubmitting(true);
    try {
      const newFollowUp = await apiClient<FollowUp>(
        `/meetings/action-items/${actionItemId}/follow-ups`,
        {
          method: 'POST',
          body: { note: followUpNote.trim() },
        },
      );
      setActionItems((prev) =>
        prev.map((item) => {
          if (item.id === actionItemId) {
            return { ...item, followUps: [...item.followUps, newFollowUp] };
          }
          return item;
        }),
      );
      setFollowUpNote('');
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to add follow-up.');
    } finally {
      setFollowUpSubmitting(false);
    }
  }

  // ── Helpers ───────────────────────────────────────────────

  function resetForm() {
    setFormTitle('');
    setFormDescription('');
    setFormAssigneeId('');
    setFormDueDate('');
    setFormPriority('Medium');
    setFormError('');
    setShowForm(false);
  }

  function formatDate(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  function formatDateTime(dateStr: string): string {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  function getTodayString(): string {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  // ── Render ────────────────────────────────────────────────

  if (loading) {
    return <div style={styles.loading}>Loading action items...</div>;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h3 style={styles.sectionTitle}>
          Action Items ({actionItems.length})
        </h3>
        {canEdit && !showForm && (
          <button style={styles.addBtn} onClick={() => setShowForm(true)}>
            + Add Action Item
          </button>
        )}
      </div>

      {/* Error */}
      {error && <div style={styles.error}>{error}</div>}

      {/* Add Action Item Form */}
      {showForm && (
        <div style={styles.formCard}>
          <h4 style={styles.formTitle}>New Action Item</h4>
          {formError && <div style={styles.error}>{formError}</div>}
          <form onSubmit={handleCreateActionItem}>
            <div style={styles.formRow}>
              <div style={styles.formFieldFull}>
                <label style={styles.label} htmlFor="ai-title">
                  Title <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <input
                  id="ai-title"
                  style={styles.input}
                  type="text"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder="Action item title"
                  maxLength={500}
                  disabled={submitting}
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formFieldFull}>
                <label style={styles.label} htmlFor="ai-description">
                  Description
                </label>
                <textarea
                  id="ai-description"
                  style={styles.textarea}
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Optional description"
                  maxLength={2000}
                  disabled={submitting}
                />
              </div>
            </div>
            <div style={styles.formRow}>
              <div style={styles.formField}>
                <label style={styles.label} htmlFor="ai-assignee">
                  Assignee <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <select
                  id="ai-assignee"
                  style={styles.select}
                  value={formAssigneeId}
                  onChange={(e) => setFormAssigneeId(e.target.value)}
                  disabled={submitting}
                >
                  <option value="">Select assignee</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.firstName} {m.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div style={styles.formField}>
                <label style={styles.label} htmlFor="ai-due-date">
                  Due Date <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <input
                  id="ai-due-date"
                  style={styles.input}
                  type="date"
                  value={formDueDate}
                  onChange={(e) => setFormDueDate(e.target.value)}
                  min={getTodayString()}
                  disabled={submitting}
                />
              </div>
              <div style={styles.formField}>
                <label style={styles.label} htmlFor="ai-priority">
                  Priority <span style={{ color: '#d32f2f' }}>*</span>
                </label>
                <select
                  id="ai-priority"
                  style={styles.select}
                  value={formPriority}
                  onChange={(e) => setFormPriority(e.target.value as ActionItemPriority)}
                  disabled={submitting}
                >
                  <option value="High">High</option>
                  <option value="Medium">Medium</option>
                  <option value="Low">Low</option>
                </select>
              </div>
            </div>
            <div style={styles.formBtnRow}>
              <button type="submit" style={styles.submitBtn} disabled={submitting}>
                {submitting ? 'Creating...' : 'Create Action Item'}
              </button>
              <button type="button" style={styles.cancelBtn} onClick={resetForm} disabled={submitting}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Action Items List */}
      {actionItems.length === 0 ? (
        <div style={styles.emptyState}>No action items yet.</div>
      ) : (
        actionItems.map((item) => {
          const isExpanded = expandedId === item.id;
          const transitions = STATUS_TRANSITIONS[item.status];

          return (
            <div key={item.id} style={styles.itemCard}>
              {/* Item Header (clickable to expand) */}
              <div
                style={styles.itemHeader}
                onClick={() => setExpandedId(isExpanded ? null : item.id)}
                role="button"
                aria-expanded={isExpanded}
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setExpandedId(isExpanded ? null : item.id);
                  }
                }}
              >
                <div style={styles.itemLeft}>
                  <span style={{ fontSize: '0.8rem', color: '#888' }}>
                    {isExpanded ? '▼' : '▶'}
                  </span>
                  <span style={styles.itemTitle}>{item.title}</span>
                  {item.overdue && (
                    <span style={styles.overdueText}>⚠ OVERDUE</span>
                  )}
                </div>
                <div style={styles.itemMeta}>
                  <span style={styles.assigneeText}>
                    {item.assignee.firstName} {item.assignee.lastName}
                  </span>
                  <span style={{
                    ...styles.dueDateText,
                    color: item.overdue ? '#d32f2f' : '#555',
                    fontWeight: item.overdue ? 700 : 400,
                  }}>
                    {formatDate(item.dueDate)}
                  </span>
                  <span
                    style={{
                      ...styles.badge,
                      backgroundColor: PRIORITY_COLORS[item.priority],
                    }}
                  >
                    {item.priority}
                  </span>
                  <span
                    style={{
                      ...styles.badge,
                      backgroundColor: STATUS_COLORS[item.status],
                    }}
                  >
                    {item.status}
                  </span>
                </div>
              </div>

              {/* Expanded Section */}
              {isExpanded && (
                <div style={styles.expandedSection}>
                  {/* Description */}
                  {item.description && (
                    <p style={styles.descriptionText}>{item.description}</p>
                  )}

                  {/* Status Transition Controls */}
                  {canEdit && transitions.length > 0 && (
                    <div style={styles.statusControls}>
                      <span style={{ fontSize: '0.8rem', color: '#555', marginRight: '0.25rem', alignSelf: 'center' }}>
                        Change status:
                      </span>
                      {transitions.map((t) => (
                        <button
                          key={t.target}
                          style={{
                            ...styles.statusBtn,
                            backgroundColor:
                              t.target === 'Cancelled'
                                ? '#9e9e9e'
                                : t.target === 'Completed'
                                  ? '#1a6b3c'
                                  : '#1976d2',
                          }}
                          onClick={() => handleStatusChange(item.id, t.target)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Follow-Up Notes Timeline */}
                  <div style={styles.followUpsSection}>
                    <h4 style={styles.followUpsTitle}>
                      Follow-Up Notes ({item.followUps.length})
                    </h4>
                    {item.followUps.length === 0 ? (
                      <p style={{ fontSize: '0.8rem', color: '#999', margin: '0.25rem 0' }}>
                        No follow-up notes yet.
                      </p>
                    ) : (
                      item.followUps.map((fu) => (
                        <div key={fu.id} style={styles.followUpItem}>
                          <p style={styles.followUpNote}>{fu.note}</p>
                          <span style={styles.followUpMeta}>
                            {formatDateTime(fu.createdAt)}
                          </span>
                        </div>
                      ))
                    )}

                    {/* Add Follow-Up Form */}
                    {canEdit && (
                      <div style={styles.followUpForm}>
                        <textarea
                          style={styles.followUpInput}
                          value={expandedId === item.id ? followUpNote : ''}
                          onChange={(e) => setFollowUpNote(e.target.value)}
                          placeholder="Add a follow-up note..."
                          maxLength={2000}
                          disabled={followUpSubmitting}
                          rows={1}
                        />
                        <button
                          style={{
                            ...styles.followUpBtn,
                            opacity: followUpSubmitting || !followUpNote.trim() ? 0.6 : 1,
                            cursor: followUpSubmitting || !followUpNote.trim() ? 'not-allowed' : 'pointer',
                          }}
                          onClick={() => handleAddFollowUp(item.id)}
                          disabled={followUpSubmitting || !followUpNote.trim()}
                        >
                          {followUpSubmitting ? 'Adding...' : 'Add Note'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
